import {type JSX, useEffect, useState} from "react";

import {useAppSelector, useEmailInput, useMdbCheckInput, useMessageInput} from "../../hooks";
import {classNames} from "../../utils";
import {useErrorNotifications, useNotifications} from "../common/Notifications";


interface ReportFormProps {
  onCreateClick: (contact: boolean, email: string, message: string) => void,
}

/**
 * Wizard report form component.
 */
export function ReportForm({onCreateClick}: Readonly<ReportFormProps>): JSX.Element {
  const initialEmail = useAppSelector<string>(state => state.user.claims?.email ?? '')
  const initialMessage = ''
  const submittingToAddress = useAppSelector<string>(state => state.report.submittingToAddress)
  const submittedToAddress = useAppSelector<string>(state => state.report.submittedToAddress)
  const notifications = useNotifications()
  useErrorNotifications((state) => state.report.error)
  const [reportSentNotificationPending, setReportSentNotificationPending] = useState<boolean>(false)


  const [contactInput, contact] = useMdbCheckInput(
    false,
    'Haluan, että minuun otetaan yhteyttä',
    {id: 'contactCheck', testId: 'contact-checkbox'})

  const [emailInput, email] = useEmailInput(
    initialEmail,
    'form-control mb-3',
    'Sähköpostiosoite')
  const [messageInput, message] = useMessageInput(
    initialMessage,
    'form-control mb-3',
    'Tähän voit halutessasi antaa lisätietoja')

  const submitting = email !== '' && submittingToAddress === email
  const submitted = submittedToAddress === email

  useEffect(() => {
    if (!reportSentNotificationPending) {
      return
    }
    if (submitted) {
      setReportSentNotificationPending(false)
      notifications.success(`Raportti lähetetty osoitteeseen '${submittedToAddress}'.`)
    }
  }, [notifications, submitted, submittedToAddress, reportSentNotificationPending]);

  function onClick() {
    if (!submitting) {
      setReportSentNotificationPending(true)
      onCreateClick(contact, email, message)
    }
  }

  const classes = {
    btn: true,
    'btn-primary': !submitted,
    'btn-light': submitted
  }

  return (
    <div>
      <div className='card shadow-sm border-0 p-4 mb-4'>
        {contactInput}
        {emailInput}
        {messageInput}
        <button data-testid='create-report' className={classNames(classes)} onClick={onClick} disabled={submitting}>
          {submitting
            ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'/>
                <span style={{verticalAlign: 'top'}}>Lähetetään...</span>
              </>
            )
            : null}
          {!submitting && submitted
            ? (
              <i className='fas fa-check text-success fa-lg me-2'/>
            )
            : null}
          {!submitting ? 'Lähetä laskelma' : null}
        </button>
      </div>
      <p data-testid='link-to-planner' className='text-muted'>Suunnittelun tuki:
        <a href='mailto:suunnittelija@koka.fi'>suunnittelija@koka.fi</a> / 010 286 2012
      </p>
    </div>
  )
}
